<template>
    <section
        v-if="!(breakpoints?.isMobile && slice.primary.hideInMobile) && slice.primary"
        :id="slice.primary.anchorName || undefined"
        ref="section"
        class="slice slice-call-to-action"
        :class="{
            'slice-call-to-action--default': isDefaultVariation,
            'cta-wrapper': !isDefaultVariation,
            'slice-cta--trends': slice.variation === 'trendsCta',
            plain: slice.variation === 'plainBackground',
            'theme-malt': theme === 'malt',
            'theme-strategy': theme === 'strategy',
            'theme-supermalter': theme === 'supermalter',
        }"
        aria-label="call-to-action"
        :data-testid="isDefaultVariation ? 'slice-call-to-action-default' : undefined"
        :data-mui-theme="theme"
    >
        <template v-if="defaultSlice">
            <div class="slice-content">
                <div class="slice-content-inner mui-background-lighter">
                    <PrismicRichText
                        :field="defaultSlice.primary.title"
                        class="slice-call-to-action--default__title mui-title"
                        data-testid="slice-call-to-action-title"
                    />
                    <PrismicRichText
                        :field="defaultSlice.primary.description"
                        class="slice-description"
                        data-testid="slice-call-to-action-description"
                    />

                    <div class="buttons-wrapper">
                        <MuiButton
                            v-if="isFilledKeyText(defaultSlice.primary.primaryCtaLink) && isFilledKeyText(defaultSlice.primary.primaryCtaLinkText)"
                            variant="main"
                            :href="defaultSlice.primary.primaryCtaLink"
                            data-component-source="call-to-action"
                            data-testid="slice-call-to-action-button"
                        >
                            {{ defaultSlice.primary.primaryCtaLinkText }}
                        </MuiButton>
                        <MuiButton
                            v-if="
                                isFilledKeyText(defaultSlice.primary.secondaryCtaLink) && isFilledKeyText(defaultSlice.primary.secondaryCtaLinkText)
                            "
                            variant="secondary"
                            :href="defaultSlice.primary.secondaryCtaLink"
                            data-component-source="call-to-action"
                        >
                            {{ defaultSlice.primary.secondaryCtaLinkText }}
                        </MuiButton>
                    </div>
                </div>
            </div>
        </template>

        <template v-else-if="trendsCtaSlice">
            <PrismicRichText :field="trendsCtaSlice.primary.title" class="title" />
            <PrismicRichText :field="trendsCtaSlice.primary.description" class="description" />

            <div class="buttons-wrapper">
                <VJoyButton
                    v-if="isFilledKeyText(trendsCtaSlice.primary.ctaLink) && isFilledKeyText(trendsCtaSlice.primary.ctaLinkText)"
                    :variant="ctaVariant"
                    :href="trendsCtaSlice.primary.ctaLink"
                    data-component-source="call-to-action"
                >
                    {{ trendsCtaSlice.primary.ctaLinkText }}
                </VJoyButton>
            </div>
        </template>

        <template v-else-if="plainBackgroundSlice">
            <PrismicRichText :field="plainBackgroundSlice.primary.title" class="title" />
            <PrismicRichText :field="plainBackgroundSlice.primary.description" class="description" />

            <div class="buttons-wrapper">
                <VJoyButton
                    v-if="
                        isFilledKeyText(plainBackgroundSlice.primary.primaryCtaLink) &&
                        isFilledKeyText(plainBackgroundSlice.primary.primaryCtaLinkText)
                    "
                    :variant="ctaVariant"
                    :href="plainBackgroundSlice.primary.primaryCtaLink"
                    data-component-source="call-to-action"
                >
                    {{ plainBackgroundSlice.primary.primaryCtaLinkText }}
                </VJoyButton>

                <VJoyButton
                    v-if="
                        isFilledKeyText(plainBackgroundSlice.primary.secondaryCtaLink) &&
                        isFilledKeyText(plainBackgroundSlice.primary.secondaryCtaLinkText)
                    "
                    variant="ghost"
                    :href="plainBackgroundSlice.primary.secondaryCtaLink"
                    data-component-source="call-to-action"
                >
                    {{ plainBackgroundSlice.primary.secondaryCtaLinkText }}
                </VJoyButton>
            </div>
        </template>
    </section>
</template>

<script setup lang="ts">
    import {computed, ref, useTemplateRef} from 'vue';
    import type {Content} from '@prismicio/client';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import {MuiButton} from '#components';
    import {useComponentTracking} from '#imports';
    import {VJoyButton, type TJoyButtonVariants} from '@maltjoy/core-vue';
    import {useBreakpoints} from '../../composables/shared/useBreakpoints';
    import {usePrismicTypeGuards} from '../../composables/shared/useTypeGuards';

    const section = useTemplateRef('section');
    const breakpoints = useBreakpoints();
    const {isFilledKeyText} = usePrismicTypeGuards();

    const props = defineProps(getSliceComponentProps<Content.CallToActionSliceSlice>());

    type CallToActionDefaultSlice = Extract<Content.CallToActionSliceSlice, {variation: 'default'}>;
    type CallToActionPlainBackgroundSlice = Extract<Content.CallToActionSliceSlice, {variation: 'plainBackground'}>;
    type CallToActionTrendsCtaSlice = Extract<Content.CallToActionSliceSlice, {variation: 'trendsCta'}>;

    const defaultSlice = computed((): CallToActionDefaultSlice | null => (props.slice.variation === 'default' ? props.slice : null));
    const isDefaultVariation = computed(() => defaultSlice.value !== null);

    const plainBackgroundSlice = computed((): CallToActionPlainBackgroundSlice | null =>
        props.slice.variation === 'plainBackground' ? props.slice : null,
    );
    const trendsCtaSlice = computed((): CallToActionTrendsCtaSlice | null => (props.slice.variation === 'trendsCta' ? props.slice : null));

    const primaryTheme = props.slice.variation === 'default' || props.slice.variation === 'plainBackground' ? props.slice.primary.theme : '';
    const theme = ref(primaryTheme);
    const ctaVariants = ref({
        malt: 'primary',
        strategy: 'main',
        supermalter: 'admin',
    });

    const ctaVariant = computed<TJoyButtonVariants>(() => ctaVariants.value[theme.value as keyof typeof ctaVariants.value] as TJoyButtonVariants);

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '../../scss/variables.scss' as vars;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-call-to-action {
        &--default {
            color: var(--joy-color-neutral-70);
            width: 100%;

            .slice-content {
                width: 100%;
            }

            .slice-content-inner {
                position: relative;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                overflow: hidden;
                padding: 64px var(--joy-core-spacing-8);
                border-radius: var(--joy-core-radius-4);
                background-image: url('./cta-shapes-card.svg');
                background-repeat: no-repeat;
                background-position: center bottom;
                background-size: contain;

                @include mq.screen_sm(min) {
                    background-size: cover;
                    padding: 64px var(--joy-core-spacing-8) 128px var(--joy-core-spacing-8);
                }
            }

            &__title {
                max-width: 650px;
                margin-bottom: var(--joy-core-spacing-1);

                * {
                    @include text.getFontBySize(secondary-600);
                }

                @include mq.screen_md(min) {
                    * {
                        @include text.getFontBySize(secondary-800);
                    }
                }
            }

            .slice-description {
                max-width: 450px;
                margin-bottom: var(--joy-core-spacing-8);

                @include text.getFontBySize(primary-300);

                @include mq.screen_md(min) {
                    @include text.getFontBySize(primary-800);
                }
            }

            .buttons-wrapper {
                z-index: 1;
                width: 100%;
                display: flex;
                justify-content: center;
                flex-direction: column;
                gap: var(--joy-core-spacing-5);
                margin-bottom: var(--joy-core-spacing-5);

                @include mq.screen_xs(min) {
                    flex-direction: row;
                }
            }
        }
    }

    .cta-wrapper {
        @include vars.flex-column-center;

        $background-width: 1658px;
        $visible-background-width: 1338px;

        width: 100%;
        padding: 100px 20px 90px;
        font-family: var(--joy-font-family-base);
        background-repeat: no-repeat;
        background-size: auto 590px;
        min-height: 590px;

        &.slice-cta--trends {
            overflow: hidden;
        }

        &:not(.slice-cta--trends) {
            background-image: url('./cta-shapes.svg');
        }

        background-position: center;

        @media (max-width: 1920px) {
            --background-position: center right calc(100vw - #{$visible-background-width} - 100px);

            background-position: var(--background-position);
        }

        @media (max-width: calc(#{$visible-background-width} / 0.80 + 100px)) {
            --background-position: center right 20vw;

            background-position: var(--background-position);
        }

        @media (max-width: calc(#{$visible-background-width} / 0.80)) {
            --background-position: center right calc(100vw - #{$visible-background-width});

            background-position: var(--background-position);
        }

        @media (max-width: $visible-background-width) {
            background-position: center right;
        }

        @media (max-width: vars.$mobile-breakpoint) {
            background-position: center;
            min-height: unset;
            background-size: auto 100%;
            padding: 125px vars.$margin-section-mobile 110px;
        }

        .title h2 {
            text-align: center;
            color: var(--joy-color-neutral-0);

            @media (max-width: vars.$mobile-breakpoint) {
                margin-bottom: 32px;
            }
        }

        .buttons-wrapper {
            width: 100%;

            > a {
                width: 100%;
                margin-bottom: var(--joy-core-spacing-5);
            }

            @include mq.screen_sm(min) {
                display: flex;
                justify-content: center;
                gap: var(--joy-core-spacing-5);
                max-width: 620px;
                margin: 0 auto;

                > a {
                    width: auto;
                    margin-bottom: 0;
                }
            }
        }

        &:not(.plain, .slice-cta--trends) {
            .title h2 {
                max-width: 620px;
                margin-bottom: 48px;

                @include vars.secondary-title;

                color: var(--joy-color-neutral-0);
            }
        }

        &.plain {
            padding: 110px 0;
            min-height: unset;
            background-image: none;
            background-color: var(--joy-color-primary-50);

            @media (max-width: vars.$mobile-breakpoint) {
                padding: 50px 0;
            }

            .title {
                @include vars.content-width;

                h2 {
                    margin-bottom: var(--joy-core-spacing-6);
                }
            }

            .description {
                margin-bottom: var(--joy-core-spacing-10);
                text-align: center;

                @include vars.content-width;

                * {
                    color: var(--joy-color-neutral-0);
                }
            }

            .buttons-wrapper {
                justify-content: center;

                @include vars.content-width;

                @media (max-width: vars.$mobile-breakpoint) {
                    padding: 0 20px;
                }
            }
        }

        &.slice-cta {
            &--trends {
                &::before {
                    content: '';
                    position: absolute;
                    left: 0;
                    top: 70px;
                    width: 100%;
                    height: 100%;
                    background: url('./cta-shape-trends.svg') no-repeat top left;
                    background-size: 50%;
                    z-index: -1;

                    @include mq.screen_xs(min) {
                        top: 40px;
                    }

                    @include mq.screen_sm(min) {
                        top: 20px;
                        background-size: auto;
                    }
                }

                &::after {
                    content: '';
                    position: absolute;
                    top: 50%;
                    left: 42%;
                    height: 60%;
                    width: 115%;
                    background: var(--joy-color-primary-50);
                    border-radius: 0 220px 220px 0;
                    transform: translate(-50%, -50%) rotate(-4deg);
                    z-index: -1;

                    @include mq.screen_sm(min) {
                        height: 80%;
                    }

                    @include mq.screen_lg(min) {
                        width: 90%;
                    }
                }

                .buttons-wrapper {
                    width: initial;

                    .joy-button {
                        background-color: transparent;
                        border-color: var(--joy-color-neutral-10);
                        font-size: var(--joy-font-size-primary-600);
                        padding-left: var(--joy-core-spacing-5);
                        padding-right: var(--joy-core-spacing-5);

                        &:hover {
                            background-color: var(--joy-color-primary-70);
                        }

                        @include mq.screen_sm(min) {
                            padding: var(--joy-core-spacing-5) var(--joy-core-spacing-9);
                        }
                    }
                }

                .description {
                    margin-bottom: var(--joy-core-spacing-4);
                    text-align: center;

                    * {
                        color: var(--joy-color-neutral-0);
                    }

                    @include mq.screen_lg(min) {
                        margin-bottom: var(--joy-core-spacing-6);
                    }
                }

                .title {
                    font-family: var(--joy-font-family-title);
                    font-weight: var(--joy-font-weight-normal);
                    font-size: 40px;

                    h1,
                    h2 {
                        font-size: inherit;
                        color: var(--joy-color-neutral-0);
                        font-weight: inherit;
                        margin-bottom: var(--joy-core-spacing-4);

                        @include mq.screen_lg(min) {
                            margin-bottom: var(--joy-core-spacing-6);
                        }
                    }

                    @include mq.screen_lg(min) {
                        font-size: var(--joy-font-size-secondary-800);
                    }

                    strong {
                        display: inline-block;
                        position: relative;
                        z-index: 1;
                        font-weight: var(--joy-font-weight-normal);
                        font-family: var(--joy-font-family-title);
                        line-height: var(--joy-line-height-small);
                        color: var(--joy-color-neutral-0);
                        padding: 0 var(--joy-core-spacing-4);

                        &::before {
                            content: '';
                            display: inline-block;
                            position: absolute;
                            left: 0;
                            top: -0.09em;
                            height: 1em;
                            width: 100%;
                            background-color: var(--joy-color-brand-primary-30);
                            border-radius: 9999px;
                            z-index: -1;
                        }
                    }
                }
            }
        }

        &.theme {
            &-strategy {
                &:not(.plain) {
                    background-image: url('./cta-shapes-ms.svg');
                }

                &.plain {
                    background-color: var(--joy-color-secondary-50);
                }
            }

            &-supermalter {
                &:not(.plain) {
                    background-image: url('./cta-shapes-supermalter.svg');
                }

                &.plain {
                    background-color: var(--joy-color-primary-50);
                }

                .joy-button_primary {
                    background-color: var(--joy-color-brand-secondary-30);
                }
            }
        }
    }
</style>
